import React from "react";
import { Tooltip } from "@mui/material";
import TableCell from "./TableCell";
import { useDispatch, useSelector } from "react-redux";
import { buildingListFields } from "../../../config/buildingTableConfig";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import useColumnResizing from "../../../../../../../hooks/useColumnResizing";
import {
  setSelectedBuilding,
  selectedBuilding,
  fetchEnrichedBuildingData,
} from "../../../../../../../redux/buildings/selectedBuildingSlice";

const BuildingTable = ({
  handleOpenViewModal,
  expanded,
  sortField,
  order,
  handleSort,
  customers,
  managementCompanies,
  agencies,
}) => {
  const dispatch = useDispatch();
  const buildings = useSelector((state) => state.buildings.buildings);
  const localSelectedBuilding = useSelector(selectedBuilding);

  const fetchedData = { customers, managementCompanies, agencies };

  const {
    headerRefs,
    headerWidths,
    tableWidths,
    handleResize,
    handleResizeStop,
  } = useColumnResizing(buildingListFields);

  const handleRowClick = (building) => {
    if (
      localSelectedBuilding &&
      localSelectedBuilding.buildingID === building.buildingID
    ) {
      dispatch(setSelectedBuilding(null));
    } else {
      // First set the basic building data
      dispatch(setSelectedBuilding(building));
      
      // Then fetch the enriched data
      dispatch(fetchEnrichedBuildingData(building.buildingID));
    }
  };

  const handleRowDoubleClick = (building) => {
    // First set the basic building data
    dispatch(setSelectedBuilding(building));
    
    // Then fetch the enriched data
    dispatch(fetchEnrichedBuildingData(building.buildingID))
      .unwrap()
      .then(() => {
        // Open the view modal after fetching the enriched data
        handleOpenViewModal(building);
      })
      .catch((error) => {
        console.error("Error fetching enriched building data:", error);
        // Still open the modal even if we couldn't get the enriched data
        handleOpenViewModal(building);
      });
  };

  const handleIdClick = (building) => {
    // First set the basic building data
    dispatch(setSelectedBuilding(building));
    
    // Then fetch the enriched data
    dispatch(fetchEnrichedBuildingData(building.buildingID))
      .unwrap()
      .then(() => {
        // Open the view modal after fetching the enriched data
        handleOpenViewModal(building);
      })
      .catch((error) => {
        console.error("Error fetching enriched building data:", error);
        // Still open the modal even if we couldn't get the enriched data
        handleOpenViewModal(building);
      });
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-max mx-auto">
        <table className="table-auto">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {buildingListFields.map((field) => (
                <th
                  key={field.key}
                  className="relative pl-4 py-2 text-left text-sss-blue-sel hover:bg-cyan-100"
                >
                  <ResizableBox
                    width={headerWidths[field.key]}
                    height={20}
                    axis="x"
                    onResize={handleResize(field.key)}
                    onResizeStop={handleResizeStop(field.key)}
                    resizeHandles={["e"]}
                    handle={
                      <span
                        className="group absolute -right-4 top-0 bottom-0 w-4 cursor-col-resize"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="absolute right-0 top-0 bottom-0 w-1 group-hover:bg-sss-blue-sel" />
                      </span>
                    }
                  >
                    <Tooltip title={field.tooltip || field.header}>
                      <span
                        ref={(el) => (headerRefs.current[field.key] = el)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(field.key);
                        }}
                        className="block w-full whitespace-nowrap overflow-hidden text-ellipsis select-none"
                      >
                        {field.label}{" "}
                        {sortField === field.key &&
                          (order === "asc" ? "▲" : "▼")}
                      </span>
                    </Tooltip>
                  </ResizableBox>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {buildings.map((building) => {
              const buildingID = Number(building.buildingID);
              const isSelected =
                localSelectedBuilding &&
                Number(localSelectedBuilding.buildingID) === buildingID;
              return (
                <tr
                  key={buildingID}
                  onClick={() => handleRowClick(building)}
                  onDoubleClick={() => handleRowDoubleClick(building)}
                  className={`border-t cursor-pointer ${
                    isSelected ? "bg-cyan-100" : "bg-transparent"
                  } ${!isSelected ? "hover:bg-white hover:shadow-lg" : ""}`}
                >
                  {buildingListFields.map((field) => (
                    <TableCell
                      key={`${building.buildingID}-${field.key}`}
                      field={field}
                      building={building}
                      handleIdClick={handleIdClick}
                      width={tableWidths[field.key]}
                      fetchedData={fetchedData}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuildingTable;
